// src/pages/ServicesPage.js
// Town Services — a directory of local services for each town
// Data is managed by admin. Businesses can claim & subscribe to manage their own listing.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { db } from '../lib/firebase';
import { getTown, getDeals, createDeal, deleteDeal, recommendService, unrecommendService, hasRecommended, requestServiceClaim, getMyClaimForService } from '../lib/db';
import toast from 'react-hot-toast';
import {
  collection, query, where, orderBy, getDocs
} from 'firebase/firestore';
import BottomNav from '../components/layout/BottomNav';
import { HeaderAvatar } from '../components/shared/HeaderAvatar';
import { PageBack } from '../components/shared/PageBack';
import { ReferBanner } from '../components/shared/ReferBanner';
import { SponsorStrip } from '../components/shared/SponsorStrip';

const CATEGORIES = [
  { value: 'all',             label: 'All',                emoji: '🏘️' },
  { value: 'health',          label: 'Health & Medical',   emoji: '🏥' },
  { value: 'legal_financial', label: 'Legal & Financial',  emoji: '💼' },
  { value: 'food_drink',      label: 'Food & Drink',       emoji: '🍽️' },
  { value: 'accommodation',   label: 'Accommodation',      emoji: '🛏️' },
  { value: 'beauty',          label: 'Beauty & Hair',      emoji: '💇' },
  { value: 'sport_leisure',   label: 'Sport & Leisure',    emoji: '⚽' },
  { value: 'civic',           label: 'Civic & Community',  emoji: '🏛️' },
  { value: 'worship',         label: 'Worship',            emoji: '⛪' },
  { value: 'education',       label: 'Education',          emoji: '🎓' },
  { value: 'transport',       label: 'Transport',          emoji: '🚌' },
  { value: 'home_trade',      label: 'Home & Trade',       emoji: '🔧' },
  { value: 'shopping',        label: 'Shops & Retail',     emoji: '🛍️' },
  { value: 'services',        label: 'Other Services',     emoji: '🏘️' },
];

// Irish mobile numbers start 083/085/086/087/089 (or +353 83/85/86/87/89) —
// landlines use other area codes and can't receive SMS, so only offer
// the Text button when the number is actually a mobile.
const isIrishMobile = (phone) => {
  if (!phone) return false;
  const digits = phone.replace(/[\s\-()]/g, '');
  const normalised = digits.startsWith('+353') ? '0' + digits.slice(4)
    : digits.startsWith('353') ? '0' + digits.slice(3)
    : digits;
  return /^08[3-9]\d{7}$/.test(normalised);
};

const ServicesPage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [town, setTown] = useState(null);
  const [services, setServices] = useState([]);
  const [deals, setDeals] = useState([]);
  const [showPostDeal, setShowPostDeal] = useState(false);
  const [dealTitle, setDealTitle] = useState('');
  const [dealDescription, setDealDescription] = useState('');
  const [dealBusinessName, setDealBusinessName] = useState('');
  const [dealBusinessAddress, setDealBusinessAddress] = useState('');
  const [dealPhone, setDealPhone] = useState('');
  const [dealEmail, setDealEmail] = useState('');
  const [dealDays, setDealDays] = useState(7);
  const [postingDeal, setPostingDeal] = useState(false);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const townId = profile?.defaultTownId || localStorage.getItem('gather-town-id');

  useEffect(() => {
    if (!townId) return;
    getTown(townId).then(setTown);
  }, [townId]);

  useEffect(() => {
    const load = async () => {
      if (!townId) return;
      setLoading(true);
      setLoadError(false);
      try {
        const q = query(
          collection(db, 'services'),
          where('townId', '==', townId),
          where('active', '==', true),
          orderBy('tier'), // featured first
          orderBy('name')
        );
        const snap = await getDocs(q);
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setServices(all.filter(sv => sv.status !== 'pending_review'));
      } catch (e) {
        console.error('ServicesPage Firestore query failed — check that the services composite index exists (Firestore Console > Indexes):', e);
        setServices([]);
        setLoadError(true);
      }
      setLoading(false);
    };
    load();
  }, [townId]);

  useEffect(() => {
    if (!townId) return;
    getDeals(townId).then(setDeals).catch(err => console.error('Deals load failed:', err));
  }, [townId]);

  const filtered = services.filter(s => {
    const matchesCat = category === 'all' || s.category === category;
    const matchesSearch = !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.description?.toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  // Featured first, then alphabetical
  const sorted = [...filtered].sort((a, b) => {
    if (a.tier === 'featured' && b.tier !== 'featured') return -1;
    if (b.tier === 'featured' && a.tier !== 'featured') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="page" style={{ background: 'var(--cream)' }}>

      <div style={{ padding: '52px 20px 0' }}><PageBack fallbackPath="/" /></div>

      {/* Header */}
      <div style={{ ...s.header, padding: '0 20px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={s.h1}>Town Services</h1>
            <div style={s.sub}>Everything you need in {town?.name || 'your town'}</div>
          </div>
          <HeaderAvatar />
        </div>

        <SponsorStrip townId={townId} page="services" />

        {/* Search */}
        <div style={s.searchBox}>
          <span style={s.searchIcon}>🔍</span>
          <input
            style={s.searchInput}
            placeholder="Search services..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button style={s.clearBtn} aria-label="Clear search" onClick={() => setSearch('')}>✕</button>
          )}
        </div>
      </div>

      {/* Add a Service entry point */}
      <div style={{ padding: '0 20px 10px' }}>
        <button onClick={() => navigate(user ? '/services/post' : '/login')} style={s.skillsBanner}>
          <span style={{ fontSize: 20 }}>➕</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--ink)' }}>Add a Service</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 500 }}>Know a business or group that should be listed?</div>
          </div>
        </button>
      </div>

      {/* Deals — time-limited offers */}
      {(deals.length > 0 || user) && (
        <div style={{ padding: '0 0 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px 8px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>🏷️ Local Deals</div>
            {user && (
              <button onClick={() => setShowPostDeal(!showPostDeal)} style={{ background: 'none', border: 'none', color: 'var(--sage-dark)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                {showPostDeal ? 'Cancel' : '+ Post a deal'}
              </button>
            )}
          </div>

          {showPostDeal && (
            <div style={{ margin: '0 20px 10px', background: 'var(--white)', borderRadius: 14, padding: 14, border: '1.5px solid var(--border)' }}>
              <input className="form-input" aria-label="Deal title" placeholder="e.g. 20% off this weekend" value={dealTitle} onChange={e => setDealTitle(e.target.value)} style={{ marginBottom: 8 }} />
              <textarea className="form-input" aria-label="Deal details" placeholder="A few more details..." value={dealDescription} onChange={e => setDealDescription(e.target.value)} rows={2} style={{ marginBottom: 8, resize: 'vertical' }} />
              <input className="form-input" aria-label="Business name" placeholder="Business name" value={dealBusinessName} onChange={e => setDealBusinessName(e.target.value)} style={{ marginBottom: 8 }} />
              <input className="form-input" aria-label="Business address" placeholder="Business address" value={dealBusinessAddress} onChange={e => setDealBusinessAddress(e.target.value)} style={{ marginBottom: 8 }} />
              <input className="form-input" type="tel" aria-label="Phone (optional)" placeholder="Phone (optional)" value={dealPhone} onChange={e => setDealPhone(e.target.value)} style={{ marginBottom: 8 }} />
              <input className="form-input" type="email" aria-label="Email (optional)" placeholder="Email (optional)" value={dealEmail} onChange={e => setDealEmail(e.target.value)} style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>Runs for:</span>
                {[1, 3, 7, 14].map(d => (
                  <button key={d} onClick={() => setDealDays(d)} style={{
                    padding: '6px 10px', borderRadius: 100, border: 'none', fontFamily: 'Nunito', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    background: dealDays === d ? 'var(--sage-dark)' : 'var(--sage-pale)', color: dealDays === d ? 'white' : 'var(--sage-dark)',
                  }}>
                    {d} day{d > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
              <button
                onClick={async () => {
                  if (!dealTitle.trim() || !dealDescription.trim()) { toast.error('Please add a title and description'); return; }
                  setPostingDeal(true);
                  try {
                    const expiresAt = new Date();
                    expiresAt.setDate(expiresAt.getDate() + dealDays);
                    await createDeal({
                      title: dealTitle.trim(), description: dealDescription.trim(),
                      businessName: dealBusinessName.trim() || null, businessAddress: dealBusinessAddress.trim() || null,
                      phone: dealPhone.trim() || null, email: dealEmail.trim() || null,
                      townId, postedBy: user.uid, expiresAt,
                    });
                    toast.success('Deal posted!');
                    setShowPostDeal(false);
                    setDealTitle(''); setDealDescription(''); setDealDays(7); setDealBusinessName(''); setDealBusinessAddress(''); setDealPhone(''); setDealEmail('');
                    getDeals(townId).then(setDeals);
                  } catch (err) {
                    console.error(err);
                    toast.error('Something went wrong — please try again');
                  }
                  setPostingDeal(false);
                }}
                disabled={postingDeal}
                style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: '11px 12px', fontSize: 13.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: postingDeal ? 0.7 : 1 }}
              >
                {postingDeal ? 'Posting...' : 'Post deal'}
              </button>
            </div>
          )}

          {deals.length > 0 && (
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '0 20px', scrollbarWidth: 'none' }}>
              {deals.map(deal => (
                <div key={deal.id} style={{ flexShrink: 0, width: 220, background: 'var(--warm-pale, #FFF3CD)', borderRadius: 14, padding: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>{deal.title}</div>
                  {deal.businessName && (
                    <div style={{ fontSize: 11.5, color: 'var(--sage-dark)', fontWeight: 700, marginBottom: 2 }}>{deal.businessName}</div>
                  )}
                  {deal.businessAddress && (
                    <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500, marginBottom: 4 }}>📍 {deal.businessAddress}</div>
                  )}
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 500, lineHeight: 1.4 }}>{deal.description}</div>
                  {(deal.phone || deal.email) && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      {deal.phone && (
                        <a href={`tel:${deal.phone}`} style={{ flex: 1, textAlign: 'center', background: 'rgba(255,255,255,0.6)', color: 'var(--sage-dark)', borderRadius: 8, padding: '6px 8px', fontSize: 11.5, fontWeight: 700, fontFamily: 'Nunito', textDecoration: 'none' }}>
                          📞 Call
                        </a>
                      )}
                      {isIrishMobile(deal.phone) && (
                        <a href={`sms:${deal.phone}`} style={{ flex: 1, textAlign: 'center', background: 'rgba(255,255,255,0.6)', color: 'var(--sage-dark)', borderRadius: 8, padding: '6px 8px', fontSize: 11.5, fontWeight: 700, fontFamily: 'Nunito', textDecoration: 'none' }}>
                          💬 Text
                        </a>
                      )}
                      {deal.email && (
                        <a href={`mailto:${deal.email}`} style={{ flex: 1, textAlign: 'center', background: 'rgba(255,255,255,0.6)', color: 'var(--sage-dark)', borderRadius: 8, padding: '6px 8px', fontSize: 11.5, fontWeight: 700, fontFamily: 'Nunito', textDecoration: 'none' }}>
                          ✉️ Email
                        </a>
                      )}
                    </div>
                  )}
                  {user && deal.postedBy === user.uid && (
                    <button
                      onClick={async () => { await deleteDeal(deal.id); setDeals(d => d.filter(x => x.id !== deal.id)); toast.success('Deal removed'); }}
                      style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Category pills */}
      <div style={s.catScroll}>
        {CATEGORIES.map(cat => (
          <button
            key={cat.value}
            style={{
              ...s.catPill,
              background: category === cat.value ? 'var(--sage-dark)' : 'var(--white)',
              color: category === cat.value ? 'white' : 'var(--ink)',
              border: category === cat.value ? 'none' : '1.5px solid var(--border)',
            }}
            onClick={() => setCategory(cat.value)}
          >
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>

      {/* Services list */}
      <div style={s.list}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        ) : loadError ? (
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <div className="empty-state-title">Couldn't load services right now</div>
            <div className="empty-state-body">Please check your connection and try again shortly.</div>
          </div>
        ) : sorted.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏘️</div>
            <div className="empty-state-title">No services listed yet</div>
            <div className="empty-state-body">Be the first to add one below, or check back soon.</div>
          </div>
        ) : sorted.map(service => (
          <ServiceCard key={service.id} service={service} user={user} />
        ))}

        {/* Claim listing CTA */}
        <div style={s.claimBox}>
          <div style={s.claimTitle}>Is your business missing?</div>
          <div style={s.claimSub}>
            Claim your free listing or subscribe to feature your business at the top of this page.
          </div>
          <a
            href="mailto:hello@gatheryourcommunity.com?subject=Services Listing Request"
            style={s.claimBtn}
          >
            Get listed →
          </a>
        </div>
      </div>

      <PageBack position="bottom" fallbackPath="/" />
      <ReferBanner />
      <BottomNav />
    </div>
  );
};

// ── Service Card ─────────────────────────────────────────────
const ServiceCard = ({ service, user }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [recommended, setRecommended] = useState(false);
  const [checkingRec, setCheckingRec] = useState(false);
  const [recCount, setRecCount] = useState(service.recommendCount || 0);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimStatus, setClaimStatus] = useState(null); // null | 'pending' | 'approved' | 'rejected'
  const [checkingClaim, setCheckingClaim] = useState(false);
  const [claimEmail, setClaimEmail] = useState('');
  const [claimPhone, setClaimPhone] = useState('');
  const [claimWebsite, setClaimWebsite] = useState('');
  const [claimMessage, setClaimMessage] = useState('');
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const isFeatured = service.tier === 'featured';
  const isOwner = user && service.claimedBy === user.uid;
  const isUnclaimed = !service.claimedBy;

  const toggleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && user && !checkingRec) {
      setCheckingRec(true);
      try {
        const already = await hasRecommended(service.id, user.uid);
        setRecommended(already);
      } catch (e) { console.error(e); }
      setCheckingRec(false);
    }
    if (next && user && isUnclaimed && !checkingClaim && claimStatus === null) {
      setCheckingClaim(true);
      try {
        const existing = await getMyClaimForService(service.id, user.uid);
        if (existing) setClaimStatus(existing.status);
      } catch (e) { console.error(e); }
      setCheckingClaim(false);
    }
  };

  const toggleRecommend = async (e) => {
    e.stopPropagation();
    if (!user) { toast.error('Sign in to recommend a service'); return; }
    try {
      if (recommended) {
        await unrecommendService(service.id, user.uid);
        setRecommended(false);
        setRecCount(c => Math.max(0, c - 1));
      } else {
        const didAdd = await recommendService(service.id, user.uid);
        if (didAdd) {
          setRecommended(true);
          setRecCount(c => c + 1);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong — please try again');
    }
  };

  const submitClaim = async () => {
    if (!claimEmail.trim()) { toast.error('Please add a contact email so we can verify'); return; }
    setSubmittingClaim(true);
    try {
      await requestServiceClaim({
        serviceId: service.id, serviceName: service.name, requestedBy: user.uid,
        contactEmail: claimEmail.trim(), contactPhone: claimPhone.trim(), website: claimWebsite.trim(), message: claimMessage.trim(),
      });
      setClaimStatus('pending');
      setShowClaimForm(false);
      toast.success("Claim submitted — we'll review it shortly");
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong — please try again');
    }
    setSubmittingClaim(false);
  };

  return (
    <div style={{
      ...cardS.card,
      border: isFeatured ? '2px solid var(--sage-light)' : '2px solid transparent',
    }}>
      {isFeatured && (
        <div style={cardS.featuredBadge}>⭐ Featured</div>
      )}

      {/* Top row */}
      <div style={cardS.topRow} onClick={toggleExpand}>
        {service.logoUrl ? (
          <img src={service.logoUrl} alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={cardS.emoji}>{service.emoji || '🏘️'}</div>
        )}
        <div style={cardS.info}>
          <div style={cardS.name}>{service.name}</div>
          <div style={cardS.desc}>{service.description}</div>
          {service.hours && (
            <div style={cardS.hours}>🕐 {service.hours}</div>
          )}
          {recCount > 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--sage-dark)', fontWeight: 600, marginTop: 4 }}>
              👍 Recommended by {recCount}
            </div>
          )}
        </div>
        <div style={cardS.chevron}>{expanded ? '∧' : '∨'}</div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={cardS.detail}>
          {service.address && (
            <div style={cardS.detailRow}>
              <span style={cardS.detailIcon}>📍</span>
              <span style={cardS.detailText}>{service.address}</span>
            </div>
          )}

          {/* Action buttons — only show if data exists */}
          <div style={cardS.actions}>
            {service.phone && (
              <a href={`tel:${service.phone}`} style={cardS.actionBtn}>
                📞 Call
              </a>
            )}
            {service.phone && isIrishMobile(service.phone) && (
              <a href={`sms:${service.phone}`} style={cardS.actionBtn}>
                💬 Text
              </a>
            )}
            {service.email && (
              <a href={`mailto:${service.email}`} style={cardS.actionBtnSecondary}>
                ✉️ Email
              </a>
            )}
            {service.website && (
              <a
                href={service.website}
                target="_blank"
                rel="noopener noreferrer"
                style={cardS.actionBtnSecondary}
              >
                🌐 Website
              </a>
            )}
          </div>

          <button
            onClick={toggleRecommend}
            style={{
              width: '100%', marginTop: 10, padding: '10px 12px', borderRadius: 12,
              border: recommended ? 'none' : '1.5px solid var(--sage-light)',
              background: recommended ? 'var(--sage-deep)' : 'var(--white)',
              color: recommended ? 'white' : 'var(--sage-dark)',
              fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer',
            }}
          >
            {recommended ? '👍 Recommended — tap to undo' : '👍 Recommend this'}
          </button>

          {/* Owner: edit link */}
          {isOwner && (
            <button
              onClick={() => navigate(`/services/edit/${service.id}`)}
              style={{ width: '100%', marginTop: 8, padding: '10px 12px', borderRadius: 12, border: 'none', background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}
            >
              ✏️ Edit my listing
            </button>
          )}

          {/* Non-owner: claim flow, only shown for genuinely unclaimed listings */}
          {!isOwner && isUnclaimed && user && (
            <div style={{ marginTop: 10 }}>
              {claimStatus === 'pending' ? (
                <div style={{ textAlign: 'center', padding: 10, fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>
                  Claim submitted — under review
                </div>
              ) : claimStatus === 'rejected' ? (
                <div style={{ textAlign: 'center', padding: 10, fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>
                  Claim wasn't approved. Contact hello@gatheryourcommunity.com if this is a mistake.
                </div>
              ) : !showClaimForm ? (
                <button
                  onClick={() => setShowClaimForm(true)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1.5px solid var(--sage-light)', background: 'var(--white)', color: 'var(--sage-dark)', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}
                >
                  🏢 Is this your business? Claim it
                </button>
              ) : (
                <div style={{ background: 'var(--cream)', borderRadius: 12, padding: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>
                    We'll review before handing over the listing
                  </div>
                  <input className="form-input" placeholder="Contact email" value={claimEmail} onChange={e => setClaimEmail(e.target.value)} style={{ marginBottom: 6 }} />
                  <input className="form-input" placeholder="Contact phone (optional)" value={claimPhone} onChange={e => setClaimPhone(e.target.value)} style={{ marginBottom: 6 }} />
                  <input className="form-input" placeholder="Website (optional)" value={claimWebsite} onChange={e => setClaimWebsite(e.target.value)} style={{ marginBottom: 6 }} />
                  <textarea className="form-input" placeholder="Anything that helps us confirm this is your business (optional)" value={claimMessage} onChange={e => setClaimMessage(e.target.value)} rows={2} style={{ marginBottom: 8, resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setShowClaimForm(false)} style={{ flex: 1, background: 'var(--sage-pale)', color: 'var(--sage-dark)', border: 'none', borderRadius: 10, padding: '9px 12px', fontSize: 12.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={submitClaim} disabled={submittingClaim} style={{ flex: 1, background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 10, padding: '9px 12px', fontSize: 12.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: submittingClaim ? 0.7 : 1 }}>
                      {submittingClaim ? 'Submitting...' : 'Submit claim'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {!isOwner && isUnclaimed && !user && (
            <button
              onClick={() => navigate('/login')}
              style={{ width: '100%', marginTop: 10, padding: '10px 12px', borderRadius: 12, border: '1.5px solid var(--sage-light)', background: 'var(--white)', color: 'var(--sage-dark)', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}
            >
              🏢 Sign in to claim this business
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────
const s = {
  skillsBanner: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    background: 'var(--sage-pale)', border: 'none', borderRadius: 16,
    padding: '14px 16px', cursor: 'pointer', textAlign: 'left',
  },
  header: { padding: '52px 20px 12px' },
  h1: { fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginBottom: 3, letterSpacing: '-0.3px' },
  sub: { fontSize: 14, color: 'var(--muted)', fontWeight: 500, marginBottom: 14 },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'var(--white)', borderRadius: 14, padding: '11px 14px',
    border: '1.5px solid var(--border)',
  },
  searchIcon: { fontSize: 16, flexShrink: 0 },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: 15, fontWeight: 500, color: 'var(--ink)', fontFamily: 'Nunito, sans-serif',
  },
  clearBtn: {
    background: 'none', border: 'none', color: 'var(--muted)',
    fontSize: 14, cursor: 'pointer', padding: '0 4px',
  },
  catScroll: {
    display: 'flex', gap: 8, overflowX: 'auto',
    padding: '10px 20px 10px', scrollbarWidth: 'none',
  },
  catPill: {
    flexShrink: 0, padding: '8px 14px', borderRadius: 100,
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'all 0.15s',
    fontFamily: 'Nunito, sans-serif',
  },
  list: { padding: '4px 18px 24px', display: 'flex', flexDirection: 'column', gap: 10 },
  claimBox: {
    background: 'var(--sage-pale)', borderRadius: 18,
    padding: 20, marginTop: 8, textAlign: 'center',
  },
  claimTitle: { fontSize: 16, fontWeight: 800, color: 'var(--ink)', marginBottom: 6 },
  claimSub: { fontSize: 13, color: 'var(--muted)', fontWeight: 500, lineHeight: 1.5, marginBottom: 14 },
  claimBtn: {
    display: 'inline-block',
    background: 'var(--sage-deep)', color: 'white',
    borderRadius: 100, padding: '10px 24px',
    fontSize: 14, fontWeight: 700, textDecoration: 'none',
  },
};

const cardS = {
  card: {
    background: 'var(--white)', borderRadius: 18,
    overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
    position: 'relative',
  },
  featuredBadge: {
    background: 'var(--warm-pale)', color: 'var(--warm-dark)',
    fontSize: 10, fontWeight: 700,
    padding: '4px 12px', textAlign: 'right',
  },
  topRow: {
    display: 'flex', gap: 12, padding: '14px 16px',
    cursor: 'pointer', alignItems: 'flex-start',
  },
  emoji: {
    fontSize: 28, flexShrink: 0,
    width: 44, height: 44,
    background: 'var(--sage-pale)', borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  info: { flex: 1, minWidth: 0 },
  name: { fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 3 },
  desc: { fontSize: 12, color: 'var(--muted)', fontWeight: 500, lineHeight: 1.45, marginBottom: 4 },
  hours: { fontSize: 11, color: 'var(--sage-deep)', fontWeight: 600 },
  chevron: { fontSize: 14, color: 'var(--muted)', flexShrink: 0, marginTop: 2 },
  detail: {
    padding: '0 16px 16px',
    borderTop: '1px solid var(--border)',
    paddingTop: 12, marginTop: 0,
  },
  detailRow: { display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 },
  detailIcon: { fontSize: 14, flexShrink: 0, marginTop: 1 },
  detailText: { fontSize: 13, color: 'var(--ink)', fontWeight: 500, lineHeight: 1.4 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  actionBtn: {
    flex: 1, minWidth: 80,
    background: 'var(--sage-deep)', color: 'white',
    borderRadius: 12, padding: '10px 14px',
    fontSize: 13, fontWeight: 700,
    textAlign: 'center', textDecoration: 'none',
    display: 'inline-block',
  },
  actionBtnSecondary: {
    flex: 1, minWidth: 80,
    background: 'var(--sage-pale)', color: 'var(--sage-dark)',
    borderRadius: 12, padding: '10px 14px',
    fontSize: 13, fontWeight: 700,
    textAlign: 'center', textDecoration: 'none',
    display: 'inline-block',
  },
};

export default ServicesPage;
